if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      // EP: variant map emitted by product-variant-picker.liquid
      get variantMap() {
        if (this._variantMap !== undefined) return this._variantMap;
        const node = this.querySelector('[data-variant-map]');
        try {
          this._variantMap = node ? JSON.parse(node.textContent) : null;
        } catch (e) {
          this._variantMap = null;
        }
        return this._variantMap;
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll('.product-form__input'));
      }

      groupSelectedValue(group) {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      }

      setGroupValue(group, value) {
        const select = group.querySelector('select');
        if (select) {
          Array.from(select.options).forEach((opt) => {
            if (opt.value === value) {
              opt.setAttribute('selected', 'selected');
              opt.selected = true;
            } else {
              opt.removeAttribute('selected');
            }
          });
          const label = group.querySelector('[data-selected-value]');
          if (label && !label.classList.contains('dropdown-swatch')) label.innerHTML = value;
          return;
        }
        Array.from(group.querySelectorAll('input[type="radio"]')).forEach((input) => {
          if (input.value === value) input.checked = true;
        });
        const label = group.querySelector('[data-selected-value]');
        if (label) label.innerHTML = value;
      }

      // EP: when the chosen combination is sold out or does not exist,
      // snap the other options to the closest in-stock variant that keeps
      // the value the customer just picked. Returns option value ids.
      snapToAvailable(target) {
        const map = this.variantMap;
        const fallback = this.selectedOptionValues;
        if (!map || !map.variants || !map.values) return fallback;

        const groups = this.optionGroups;
        if (!groups.length) return fallback;

        const changedGroup = target.closest('.product-form__input');
        const changedIdx = groups.indexOf(changedGroup);
        if (changedIdx < 0) return fallback;

        const current = groups.map((g) => this.groupSelectedValue(g));
        const exact = map.variants.find((v) => v.o.every((val, i) => val === current[i]));
        if (exact && exact.a) return fallback;

        const candidates = map.variants.filter((v) => v.a && v.o[changedIdx] === current[changedIdx]);
        if (!candidates.length) return fallback;

        // Prefer keeping the customer's other selections; on a tie, prefer the
        // value that appears first in the option's own order (e.g. smallest size).
        const orderIndex = (i, val) => {
          const num = String(val).match(/(\d+(\.\d+)?)/);
          if (num) return parseFloat(num[1]);
          const keys = Object.keys(map.values[i] || {});
          const idx = keys.indexOf(val);
          return 1000 + (idx < 0 ? keys.length : idx);
        };
        let best = null;
        let bestScore = -1;
        let bestOrder = Infinity;
        candidates.forEach((c) => {
          const score = c.o.reduce((n, val, i) => n + (i !== changedIdx && val === current[i] ? 1 : 0), 0);
          const order = c.o.reduce((n, val, i) => n + (i !== changedIdx ? orderIndex(i, val) : 0), 0);
          if (score > bestScore || (score === bestScore && order < bestOrder)) {
            best = c;
            bestScore = score;
            bestOrder = order;
          }
        });

        best.o.forEach((val, i) => {
          if (i === changedIdx) return;
          this.setGroupValue(groups[i], val);
        });

        const ids = best.o.map((val, i) => {
          const lookup = map.values[i] || {};
          return lookup[val];
        });
        if (ids.some((id) => id === undefined || id === null)) return this.selectedOptionValues;
        return ids.map(String);
      }

      connectedCallback() {
        if (this._hasChangeListener) {
          return;
        }

        this._hasChangeListener = true;

        this.addEventListener(
          'change',
          (event) => {
            // Check if target is inside this element
            const targetInThisElement = this.contains(event.target);

            // Check if there's a nested variant-selects between this and target
            let hasNestedVariantSelects = false;
            let currentElement = event.target;

            while (currentElement && currentElement !== document.body) {
              if (currentElement.tagName === 'VARIANT-SELECTS') {
                if (currentElement !== this) {
                  hasNestedVariantSelects = true;
                  break;
                } else {
                  break;
                }
              }
              currentElement = currentElement.parentElement;
            }

            // Ignore if target is not in this element or there's a nested variant-selects
            if (!targetInThisElement || hasNestedVariantSelects) {
              return;
            }

            const target = this.getInputForEventTarget(event.target);
            this.updateSelectedSwatchValue(event);
            const selectedOptionValues = this.snapToAvailable(event.target);
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event,
                target,
                selectedOptionValues,
              },
            });
          },
          true
        ); // Use CAPTURE phase to catch events before they bubble
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}
